<template>
  <page-template :is-loading="isLoading" :data-exists="loadBalancers !== null">
    <template #pageHeadline>
      {{ $t('sslLoadBalancer.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="loadBalancers"
        :sticky-header="true"
        :show-expander-column="true"
        :show-download-button="false"
        :show-new-button="false"
        :show-filter-row="true"
        class="rbz-list-table"
        data-qa="ssl-load-balancers-table"
      >
        <template #rowExpander="rowProps">
          <div class="load-balancer-row-content">
            <div class="certificates-section">
              <div class="rbz-field default-certificate">
                <label class="rbz-label">
                  {{ $t('sslLoadBalancer.defaultCertificate') }}
                </label>
                <ssl-load-balancers-certificate-card
                  :certificate-details="getCertificateDetails(rowProps.row['default_certificate'])"
                />
              </div>
              <div v-if="rowProps.row.certificates?.length" class="rbz-field certificate">
                <label class="rbz-label">
                  {{ $t('sslLoadBalancer.certificates') }}
                </label>
                <ssl-load-balancers-certificate-card
                  v-for="certificate in rowProps.row.certificates"
                  :key="
                    getCertificateDetails(certificate)?.id ||
                    findLocalCertificateNameWithLink(certificate)
                  "
                  :certificate-details="getCertificateDetails(certificate)"
                >
                  <template #buttons>
                    <rbz-form-button
                      :label="$t('sslLoadBalancer.certificateCard.setDefault')"
                      @button-click="setDefaultCertificate(rowProps.row, certificate)"
                      class="set-default-certificate-button"
                      data-qa="set-default-certificate-button"
                    />
                    <rbz-form-button
                      :label="$t('sslLoadBalancer.certificateCard.detach')"
                      @button-click="detachNonDefaultCertificate(rowProps.row, certificate)"
                      class="detach-certificate-button"
                      data-qa="detach-certificate-button"
                    />
                  </template>
                </ssl-load-balancers-certificate-card>
              </div>
              <rbz-form-button
                v-if="isAttachButtonEnabled(rowProps.row)"
                :label="$t('sslLoadBalancer.attach')"
                variant="secondary"
                @button-click="openAttachCertificateModal(rowProps.row)"
                class="attach-certificate-button"
                data-qa="attach-certificate-button"
              />
              <span v-else class="rbz-text-danger quota-reached-message">
                {{ $t('sslLoadBalancer.quotaReached', { provider: rowProps.row['provider'] }) }}
              </span>
            </div>
            <div class="region-section" v-if="getLoadBalancerRegions(rowProps.row)?.length">
              <div class="rbz-columns">
                <div class="rbz-column is-4">
                  <div class="rbz-field">
                    <rbz-dropdown
                      :items="getLoadBalancerRegions(rowProps.row)"
                      :value="getCurrentLoadBalancerRegion(rowProps.row)"
                      @update:value="
                        setCurrentLoadBalancerRegion(rowProps.row, $event as string);
                        saveRegions();
                      "
                      :label="$t('sslLoadBalancer.preferredDatacenter')"
                      :placeholder="$t('sslLoadBalancer.preferredDatacenterPlaceholder')"
                      class="preferred-datacenter-selection"
                      data-qa="preferred-datacenter-dropdown"
                      :can-clear-single-select="true"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </rbz-table>
      <rbz-modal-dialog
        :show="showAttachCertificateModal"
        :title="$t('sslLoadBalancer.attachModal.title')"
        :subtitle="
          $t('sslLoadBalancer.attachModal.subtitle', { loadBalancer: selectedLoadBalancer?.name })
        "
        :secondary-btn-title="$t('common.operations.cancel.label')"
        @onSecondaryBtnClick="showAttachCertificateModal = false"
        @closeModal="showAttachCertificateModal = false"
        size="medium"
      >
        <template #content>
          <rbz-table
            :columns="attachCertificateModalColumns"
            :table-data="certificatesToAttach"
            :row-buttons="attachCertificateModalRowButtons"
            :show-download-button="false"
            :show-new-button="false"
            :show-filter-row="true"
            data-qa="certificates-table"
            @attach-row-button-clicked="
              attachCertificateToLoadBalancer(selectedLoadBalancer, $event.id, false)
            "
          >
          </rbz-table>
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import {
  Balancer,
  Certificate,
  CertificateDetails,
  GenericObject,
  Link,
  LoadBalancerRegionsConfig,
} from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import { mapStores } from 'pinia';
import PageTemplate from '@/views/PageTemplate.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import SslLoadBalancersCertificateCard from '@/components/SslLoadBalancersCertificateCard.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import RbzDropdown from '@/components/RbzDropdown.vue';
import {
  shouldUseMockData,
  getSslLoadBalancersMockData,
  getSslLoadBalancersRegionsMockData,
  getCertificatesMockData,
} from '@/helpers/MockDataUtils';

const MAX_CERT_PER_LB: { [_key in Balancer['load_balancer_type']]: number } = {
  gcp: 15,
  application: 25,
  classic: 1,
  link11: 0,
};

export default defineComponent({
  name: 'SslLoadBalancers',
  components: {
    RbzDropdown,
    SslLoadBalancersCertificateCard,
    RbzFormButton,
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },
  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if ((this.$route.name as string).includes('Load Balancers') && val && val !== oldVal) {
          this.setPageLoadingStatus(true);
          await this.loadData();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('sslLoadBalancer.name'),
          fieldNames: ['name'],
          displayFunction: (item) => {
            return `${item.name}:${item.listener_port}`;
          },
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          autoFocus: true,
        },
        {
          id: 'certificates-count',
          title: this.$t('sslLoadBalancer.numberOfCertificates'),
          fieldNames: ['cert'],
          displayFunction: (item) => {
            const certificatesCount = item.certificates?.length || 0;
            let defaultCertificateNumber = 0;
            if (item.default_certificate) {
              defaultCertificateNumber = 1;
            }
            let maxCerts;
            switch (item.provider) {
              case 'aws': {
                maxCerts = MAX_CERT_PER_LB['application'];
                break;
              }
              case 'gcp': {
                maxCerts = MAX_CERT_PER_LB['gcp'];
                break;
              }
              case 'link11': {
                maxCerts = MAX_CERT_PER_LB['link11'];
                break;
              }
              default: {
                maxCerts = 0;
                break;
              }
            }
            if (maxCerts === 0) {
              return `${certificatesCount + defaultCertificateNumber}`;
            } else {
              return `${certificatesCount + defaultCertificateNumber}/${maxCerts}`;
            }
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-150px',
        },
        {
          id: 'cloud-provider',
          title: this.$t('sslLoadBalancer.cloudProvider'),
          fieldNames: ['provider'],
          displayFunction: (item) => {
            const key = `certificate.serverCertificate.${item.provider}`;
            return this.$te(key)
              ? this.$t(key)
              : item.provider.charAt(0).toUpperCase() + item.provider.slice(1);
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-140px',
          cellContentClasses: 'white-space-pre-line',
        },
        {
          id: 'dns-name',
          title: this.$t('sslLoadBalancer.ipFqdn'),
          fieldNames: ['dns_name'],
          isSortable: true,
          isSearchable: true,
          classes: 'width-140px',
          cellContentClasses: 'white-space-pre-line ellipsis',
        },
        {
          id: 'region',
          title: this.$t('sslLoadBalancer.region'),
          fieldNames: ['region'],
          isSortable: true,
          isSearchable: true,
          classes: 'width-100px',
          cellContentClasses: 'white-space-pre-line',
        },
      ] as RbzTableColumnOptions[],
      loadBalancers: null as Balancer[] | null,
      certificates: null as Certificate[] | null,
      regionsConfig: null as LoadBalancerRegionsConfig | null,
      pageLoadingCounter: null as number | null,

      showAttachCertificateModal: false,
      selectedLoadBalancer: null as Balancer | null,
      attachCertificateModalColumns: [
        {
          id: 'name',
          title: this.$t('sslLoadBalancer.attachModal.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'expiration-date',
          title: this.$t('sslLoadBalancer.attachModal.expirationDate'),
          fieldNames: ['exp_date'],
          isSortable: true,
          isSearchable: true,
          classes: 'width-150px',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      attachCertificateModalRowButtons: [
        {
          id: 'attach',
          label: this.$t('sslLoadBalancer.attachModal.attachButtonLabel'),
          variant: 'secondary',
        },
      ] as RbzTableRowButton[],
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    certificatesToAttach(): Certificate[] {
      let currentLoadBalancerCertificates: string | string[] =
        this.selectedLoadBalancer.certificates.slice();
      currentLoadBalancerCertificates.push(this.selectedLoadBalancer.default_certificate);
      currentLoadBalancerCertificates = currentLoadBalancerCertificates.join();
      return _.filter(this.certificates, (certificate: Certificate) => {
        switch (this.selectedLoadBalancer.provider) {
          case 'aws': {
            const awsLinks = _.filter(certificate?.provider_links, (link: Link) => {
              return link.provider === 'aws';
            });
            return (
              !awsLinks.length ||
              _.some(awsLinks, (awsLink: Link) => {
                return !currentLoadBalancerCertificates.includes(awsLink.link);
              })
            );
          }
          case 'gcp': {
            const gcpLinks = _.filter(certificate.provider_links, (link: Link) => {
              return link.provider === 'gcp';
            });
            return (
              !gcpLinks.length ||
              _.some(gcpLinks, (gcpLink: Link) => {
                return !currentLoadBalancerCertificates.includes(gcpLink.link);
              })
            );
          }
          case 'link11': {
            const link11Links = _.filter(certificate.provider_links, (link: Link) => {
              return link.provider === 'link11';
            });
            return (
              !link11Links.length ||
              _.some(link11Links, (link11Link: Link) => {
                return !currentLoadBalancerCertificates.includes(link11Link.link);
              })
            );
          }
          default: {
            return true;
          }
        }
      });
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter = (this.pageLoadingCounter ?? 0) + 1;
      } else {
        this.pageLoadingCounter = (this.pageLoadingCounter ?? 0) - 1;
      }
    },

    async loadData() {
      this.setPageLoadingStatus(true);
      const loadBalancersPromise = this.loadLoadBalancers();
      const certificatesPromise = this.loadCertificates();
      const regionsPromise = this.loadRegions();
      await Promise.all([loadBalancersPromise, certificatesPromise, regionsPromise]);
      this.setPageLoadingStatus(false);
    },

    async loadLoadBalancers() {
      this.setPageLoadingStatus(true);

      if (shouldUseMockData()) {
        this.loadBalancers = (getSslLoadBalancersMockData() as Balancer[]).map((balancer) => {
          return {
            ...balancer,
            id: `ui-lb-${DatasetsUtils.generateUUID2()}`,
          };
        });
        this.setPageLoadingStatus(false);
        return;
      }

      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/load-balancers`,
      });
      this.loadBalancers = response?.data?.items || [];
      this.loadBalancers = this.loadBalancers.map((balancer) => {
        return {
          ...balancer,
          id: `ui-lb-${DatasetsUtils.generateUUID2()}`,
        };
      });
      this.setPageLoadingStatus(false);
    },

    async loadCertificates() {
      this.setPageLoadingStatus(true);

      if (shouldUseMockData()) {
        let certificates = getCertificatesMockData() as Certificate[];
        this.certificates = certificates.filter((certificate: Certificate) => {
          return certificate.side === 'server';
        });
        this.setPageLoadingStatus(false);
        return;
      }

      const url = `conf/${this.selectedBranch}/certificates`;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
      });
      let certificates = response?.data?.items || [];
      this.certificates = certificates.filter((certificate: Certificate) => {
        return certificate.side === 'server';
      });
      this.setPageLoadingStatus(false);
    },

    async loadRegions() {
      this.setPageLoadingStatus(true);

      if (shouldUseMockData()) {
        this.regionsConfig = getSslLoadBalancersRegionsMockData();
        this.setPageLoadingStatus(false);
        return;
      }

      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/load-balancers/regions`,
      });
      this.regionsConfig = response?.data || {};
      this.setPageLoadingStatus(false);
    },

    async saveRegions() {
      this.setPageLoadingStatus(true);
      const regionsToSave = {
        lbs: [] as { id: string; regions: GenericObject }[],
      };
      if (!this.regionsConfig?.lbs) {
        this.setPageLoadingStatus(false);
        return;
      }
      _.forEach(this.regionsConfig.lbs, (loadBalancer) => {
        regionsToSave.lbs.push({
          id: loadBalancer.id || '',
          regions: _.cloneDeep(loadBalancer.regions),
        });
      });
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url: `conf/${this.selectedBranch}/load-balancers/regions`,
        data: regionsToSave,
      });
      this.setPageLoadingStatus(false);
    },

    getLoadBalancerRegions(loadBalancer: GenericObject): [string, string][] {
      const balancer = loadBalancer as Balancer;
      if (!this.regionsConfig?.lbs) {
        return [];
      }
      const loadBalancerRegionConfig = Object.values(this.regionsConfig.lbs).find((item) => {
        return item.name === balancer.name;
      });
      if (!loadBalancerRegionConfig) {
        return [];
      }
      const regionNames = this.regionsConfig.city_codes || {};
      return _.map(loadBalancerRegionConfig.upstream_regions, (regionCode) => {
        return [regionCode, regionNames[regionCode] || ''] as [string, string];
      });
    },

    getCurrentLoadBalancerRegion(loadBalancer: GenericObject) {
      const balancer = loadBalancer as Balancer;
      if (!this.regionsConfig?.lbs) {
        return null;
      }
      const loadBalancerRegionConfig = Object.values(this.regionsConfig.lbs).find((item) => {
        return item.name === balancer.name;
      });
      if (!loadBalancerRegionConfig) {
        return null;
      }
      const region = Object.values(loadBalancerRegionConfig.regions)[0];
      return region === 'automatic' ? null : (region as string | null);
    },

    setCurrentLoadBalancerRegion(loadBalancer: GenericObject, newRegionCode: string | null) {
      const balancer = loadBalancer as Balancer;
      if (!this.regionsConfig?.lbs) {
        return;
      }
      const loadBalancerRegionConfig = Object.values(this.regionsConfig.lbs).find((item) => {
        return item.name === balancer.name;
      });
      if (!loadBalancerRegionConfig) {
        return;
      }
      _.forEach(Object.keys(loadBalancerRegionConfig.regions), (regionCode) => {
        loadBalancerRegionConfig.regions[regionCode] = newRegionCode || 'automatic';
      });
    },

    getCertificateDetails(certificateLink: string): CertificateDetails {
      const linkToCertificatesMapId = this.findLocalCertificateNameWithLink(certificateLink);
      const certificate: Certificate = this.certificates.find((certificate: Certificate) => {
        return certificate.id === linkToCertificatesMapId;
      });
      return certificate?.subject
        ? {
            id: certificate.id,
            san: certificate.san,
            expDate: certificate['exp_date'],
            cn: new URLSearchParams(certificate.subject.replaceAll(', ', '&')).get('CN'),
            name: certificate.name,
          }
        : null;
    },

    isAttachButtonEnabled(loadBalancer: GenericObject) {
      const balancer = loadBalancer as Balancer;
      if (balancer['load_balancer_type'] === 'link11') {
        return true;
      } else {
        const maxCertsNumber = MAX_CERT_PER_LB[balancer['load_balancer_type']];
        const certificatesCount = balancer.certificates?.length || 0;
        return certificatesCount + 1 < maxCertsNumber && maxCertsNumber !== 1;
      }
    },

    findLocalCertificateNameWithLink(providerLink: string) {
      const certificate = _.find(this.certificates, (certificate: Certificate) => {
        const link: Link = _.find(certificate.provider_links, (link) => {
          return link.link === providerLink;
        });
        return !!link;
      });
      if (certificate) {
        return certificate.id;
      }
      const defaultCertName = providerLink.split('/');
      return defaultCertName[defaultCertName.length - 1] + '(*)';
    },

    async setDefaultCertificate(balancer: GenericObject, certificateLink: string) {
      const balancerTyped = balancer as Balancer;
      this.setPageLoadingStatus(true);
      let certificate = this.findLocalCertificateNameWithLink(certificateLink);
      if (certificate.includes('(*)')) {
        certificate = certificateLink;
      }
      await this.attachCertificateToLoadBalancer(balancerTyped, certificate, true);
      this.setPageLoadingStatus(false);
    },

    async attachCertificateToLoadBalancer(
      balancer: Balancer,
      certificate: string,
      isDefault: boolean
    ) {
      this.setPageLoadingStatus(true);
      const encodedBalancerName = encodeURIComponent(balancer.name);
      const encodedCertificateId = encodeURIComponent(certificate);
      const encodedBalancerProvider = encodeURIComponent(balancer.provider);
      const encodedBalancerRegion = encodeURIComponent(balancer.region);
      const encodedBalancerListenerName = encodeURIComponent(balancer['listener_name']);
      const encodedBalancerListenerPort = encodeURIComponent(balancer['listener_port']);
      const elbVersion = balancer['load_balancer_type'] !== 'classic';
      const url = `conf/${this.selectedBranch}/load-balancers/${encodedBalancerName}/certificates/${encodedCertificateId}?provider=${encodedBalancerProvider}&default=${isDefault}&region=${encodedBalancerRegion}&listener=${encodedBalancerListenerName}&listener-port=${encodedBalancerListenerPort}&elbv2=${elbVersion}`;
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'PUT',
        url: url,
      });
      this.showAttachCertificateModal = false;
      await this.loadData();
      this.setPageLoadingStatus(false);
    },

    async detachNonDefaultCertificate(balancer: GenericObject, certificateLink: string) {
      const balancerTyped = balancer as Balancer;
      this.setPageLoadingStatus(true);
      const encodedBalancerName = encodeURIComponent(balancerTyped.name);
      const encodedCertificateLink = encodeURIComponent(certificateLink);
      const encodedBalancerProvider = encodeURIComponent(balancerTyped.provider);
      const encodedBalancerRegion = encodeURIComponent(balancerTyped.region);
      const encodedBalancerListenerName = encodeURIComponent(balancerTyped['listener_name']);
      const encodedBalancerListenerPort = encodeURIComponent(balancerTyped['listener_port']);
      const elbVersion = balancerTyped['load_balancer_type'] !== 'classic';
      const url = `conf/${this.selectedBranch}/load-balancers/${encodedBalancerName}/certificates?certificate-id=${encodedCertificateLink}&provider=${encodedBalancerProvider}&region=${encodedBalancerRegion}&listener=${encodedBalancerListenerName}&listener-port=${encodedBalancerListenerPort}&elbv2=${elbVersion}`;
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: url,
      });
      await this.loadData();
      this.setPageLoadingStatus(false);
    },

    openAttachCertificateModal(loadBalancer: GenericObject) {
      this.showAttachCertificateModal = true;
      this.selectedLoadBalancer = loadBalancer as Balancer;
    },
  },
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use 'ui-component-library/assets/styles/colors' as colors;

.load-balancer-row-content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}
</style>
